/**
 * Financial Services Industry Configuration
 *
 * Supports: Loan Portfolios, Trading Systems, ATM Networks, Payment Gateways, Risk Models
 */

import { IndustryConfig } from '../industryTypes'

export const financialConfig: IndustryConfig = {
  id: 'financial',
  name: 'Financial Services',
  description: 'Risk management and financial asset monitoring',
  icon: 'Landmark',

  terminology: {
    asset: 'Portfolio',
    assets: 'Portfolios',
    assetPlural: 'Portfolios',
    assetType: 'Asset Class',
    failure: 'Default',
    health: 'Performance',
    healthScore: 'Risk Score',
    healthy: 'Performing',
    degraded: 'At Risk',
    critical: 'Non-Performing',
    alert: 'Risk Alert',
    process: 'Transaction',
    maintenance: 'Review',
    inspection: 'Audit',
    downtime: 'Disruption',
    workOrder: 'Action Item',
    prediction: 'Forecast',
    anomaly: 'Anomaly',
    trend: 'Trend',
    risk: 'Exposure',
    facility: 'Institution',
    facilityPlural: 'Institutions',
    location: 'Region',
    operator: 'Analyst',
    supervisor: 'Risk Manager',
    calibration: 'Model Calibration',
    compliance: 'Regulatory Compliance',
    criticality: 'Risk Priority',
  },

  assetTypes: [
    {
      id: 'loan_portfolio',
      name: 'Loan Portfolio',
      icon: 'Wallet',
      description: 'Consumer and commercial loan portfolios',
      category: 'Credit',
      defaultMetrics: ['default_rate', 'delinquency_rate', 'loss_severity', 'recovery_rate'],
      maintenanceTypes: ['credit_review', 'covenant_monitoring', 'collateral_revaluation', 'stress_testing'],
      criticalityLevels: [
        { id: 'critical', name: 'Systemic', color: '#ef4444', description: 'Large concentrated exposure', downtimeCostMultiplier: 100 },
        { id: 'high', name: 'Material', color: '#f59e0b', description: 'Significant exposure', downtimeCostMultiplier: 50 },
        { id: 'medium', name: 'Notable', color: '#3b82f6', description: 'Moderate exposure', downtimeCostMultiplier: 10 },
        { id: 'low', name: 'Standard', color: '#6b7280', description: 'Diversified exposure', downtimeCostMultiplier: 2 },
      ],
    },
    {
      id: 'trading_system',
      name: 'Trading System',
      icon: 'LineChart',
      description: 'Automated trading and execution systems',
      category: 'Technology',
      defaultMetrics: ['latency', 'fill_rate', 'error_rate', 'throughput'],
      maintenanceTypes: ['system_update', 'algo_tuning', 'risk_calibration', 'disaster_recovery'],
      criticalityLevels: [
        { id: 'critical', name: 'Primary', color: '#ef4444', description: 'Primary execution venue', downtimeCostMultiplier: 500 },
        { id: 'high', name: 'Secondary', color: '#f59e0b', description: 'Secondary venue', downtimeCostMultiplier: 100 },
        { id: 'medium', name: 'Tertiary', color: '#3b82f6', description: 'Backup system', downtimeCostMultiplier: 20 },
        { id: 'low', name: 'Test', color: '#6b7280', description: 'Testing environment', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'atm_network',
      name: 'ATM Network',
      icon: 'CreditCard',
      description: 'Automated teller machine network',
      category: 'Infrastructure',
      defaultMetrics: ['availability', 'transaction_success', 'cash_utilization', 'error_rate'],
      maintenanceTypes: ['cash_replenishment', 'hardware_service', 'software_update', 'security_audit'],
      criticalityLevels: [
        { id: 'critical', name: 'High Traffic', color: '#ef4444', description: 'High-volume location', downtimeCostMultiplier: 50 },
        { id: 'high', name: 'Branch', color: '#f59e0b', description: 'Branch ATM', downtimeCostMultiplier: 20 },
        { id: 'medium', name: 'Offsite', color: '#3b82f6', description: 'Offsite ATM', downtimeCostMultiplier: 5 },
        { id: 'low', name: 'Low Traffic', color: '#6b7280', description: 'Low-volume location', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'payment_gateway',
      name: 'Payment Gateway',
      icon: 'ArrowRightLeft',
      description: 'Payment processing systems',
      category: 'Technology',
      defaultMetrics: ['transaction_volume', 'success_rate', 'latency', 'fraud_rate'],
      maintenanceTypes: ['capacity_review', 'security_patch', 'fraud_rule_update', 'pci_audit'],
      criticalityLevels: [
        { id: 'critical', name: 'Primary', color: '#ef4444', description: 'Primary gateway', downtimeCostMultiplier: 1000 },
        { id: 'high', name: 'Secondary', color: '#f59e0b', description: 'Failover gateway', downtimeCostMultiplier: 200 },
        { id: 'medium', name: 'Regional', color: '#3b82f6', description: 'Regional processor', downtimeCostMultiplier: 50 },
        { id: 'low', name: 'Test', color: '#6b7280', description: 'Test environment', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'risk_model',
      name: 'Risk Model',
      icon: 'Shield',
      description: 'Quantitative risk assessment models',
      category: 'Analytics',
      defaultMetrics: ['model_accuracy', 'backtest_score', 'drift_score', 'coverage'],
      maintenanceTypes: ['model_validation', 'recalibration', 'backtest', 'regulatory_review'],
      criticalityLevels: [
        { id: 'critical', name: 'Regulatory', color: '#ef4444', description: 'Regulatory capital model', downtimeCostMultiplier: 500 },
        { id: 'high', name: 'Pricing', color: '#f59e0b', description: 'Pricing model', downtimeCostMultiplier: 100 },
        { id: 'medium', name: 'Internal', color: '#3b82f6', description: 'Internal risk model', downtimeCostMultiplier: 20 },
        { id: 'low', name: 'Experimental', color: '#6b7280', description: 'Research model', downtimeCostMultiplier: 2 },
      ],
    },
  ],

  kpiDomains: [
    {
      id: 'credit_risk',
      name: 'Credit Risk',
      icon: 'AlertTriangle',
      description: 'Credit quality and default risk metrics',
      businessValue: 'Minimize credit losses and optimize risk-adjusted returns',
      category: 'industry_specific',
      metrics: [
        { id: 'default_rate', name: 'Default Rate', unit: '%', description: 'Percentage of loans in default', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 2, warning: 4, critical: 7 } },
        { id: 'delinquency_rate', name: 'Delinquency Rate', unit: '%', description: '30+ days past due', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 3, warning: 5, critical: 8 } },
        { id: 'loss_severity', name: 'Loss Severity', unit: '%', description: 'Loss given default', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 30, warning: 45, critical: 60 } },
        { id: 'expected_loss', name: 'Expected Loss', unit: '$M', description: 'Expected credit loss', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 10, warning: 25, critical: 50 } },
      ],
      thresholds: { healthy: { max: 2 }, warning: { min: 2, max: 5 }, critical: { min: 5 } },
      visualizations: ['line_chart', 'distribution', 'heatmap', 'trend'],
    },
    {
      id: 'market_risk',
      name: 'Market Risk',
      icon: 'TrendingDown',
      description: 'Market exposure and volatility metrics',
      businessValue: 'Manage market exposure within risk appetite',
      category: 'industry_specific',
      metrics: [
        { id: 'var_99', name: 'Value at Risk (99%)', unit: '$M', description: 'Daily VaR at 99% confidence', dataType: 'continuous', aggregation: 'max', goodDirection: 'lower', defaultThresholds: { healthy: 10, warning: 20, critical: 35 } },
        { id: 'volatility', name: 'Portfolio Volatility', unit: '%', description: 'Annualized volatility', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 15, warning: 25, critical: 40 } },
        { id: 'sharpe_ratio', name: 'Sharpe Ratio', unit: '', description: 'Risk-adjusted return', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 1.5, warning: 1.0, critical: 0.5 } },
      ],
      thresholds: { healthy: { max: 10 }, warning: { min: 10, max: 25 }, critical: { min: 25 } },
      visualizations: ['line_chart', 'distribution', 'radar', 'comparison'],
    },
    {
      id: 'operational_risk',
      name: 'Operational Risk',
      icon: 'AlertOctagon',
      description: 'System reliability and operational loss metrics',
      businessValue: 'Minimize operational losses and ensure service continuity',
      category: 'universal',
      metrics: [
        { id: 'system_availability', name: 'System Availability', unit: '%', description: 'Uptime percentage', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 99.9, warning: 99.5, critical: 99.0 } },
        { id: 'transaction_error_rate', name: 'Error Rate', unit: '%', description: 'Failed transactions', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 0.1, warning: 0.5, critical: 1.0 } },
        { id: 'op_loss', name: 'Operational Loss', unit: '$K', description: 'Losses from operational failures', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 50, warning: 100, critical: 250 } },
      ],
      thresholds: { healthy: { min: 99.9 }, warning: { min: 99.0, max: 99.9 }, critical: { max: 99.0 } },
      visualizations: ['gauge', 'line_chart', 'bar_chart', 'trend'],
    },
    {
      id: 'fraud_detection',
      name: 'Fraud Detection',
      icon: 'Eye',
      description: 'Fraud monitoring and detection metrics',
      businessValue: 'Detect and prevent fraudulent activity',
      category: 'industry_specific',
      metrics: [
        { id: 'fraud_rate', name: 'Fraud Rate', unit: 'bps', description: 'Fraud as basis points of volume', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 5, warning: 10, critical: 20 } },
        { id: 'detection_rate', name: 'Detection Rate', unit: '%', description: 'Fraud caught by systems', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 95, warning: 90, critical: 80 } },
        { id: 'false_positive_rate', name: 'False Positive Rate', unit: '%', description: 'Legitimate flagged as fraud', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 5, warning: 10, critical: 20 } },
      ],
      thresholds: { healthy: { max: 5 }, warning: { min: 5, max: 15 }, critical: { min: 15 } },
      visualizations: ['line_chart', 'heatmap', 'distribution', 'comparison'],
    },
  ],

  maintenanceTypes: [
    {
      id: 'model_validation',
      name: 'Model Validation',
      description: 'Independent validation of risk models',
      category: 'condition_based',
      defaultInterval: { value: 12, unit: 'months' },
      laborRequirements: [
        { role: 'Quantitative Analyst', count: 2, hourlyRate: 150 },
        { role: 'Model Validator', count: 1, hourlyRate: 175 },
      ],
      estimatedDuration: { min: 14, typical: 28, max: 56, unit: 'days' },
      skillsRequired: ['quantitative_analysis', 'statistics', 'regulatory_knowledge'],
      partsCommonly: ['validation_software', 'data_sets'],
    },
    {
      id: 'stress_testing',
      name: 'Stress Testing',
      description: 'Portfolio stress test and scenario analysis',
      category: 'preventive',
      defaultInterval: { value: 3, unit: 'months' },
      laborRequirements: [
        { role: 'Risk Analyst', count: 2, hourlyRate: 120 },
      ],
      estimatedDuration: { min: 7, typical: 14, max: 28, unit: 'days' },
      skillsRequired: ['risk_modeling', 'scenario_analysis'],
      partsCommonly: ['scenario_data', 'computing_resources'],
    },
    {
      id: 'security_audit',
      name: 'Security Audit',
      description: 'Cybersecurity and access control audit',
      category: 'preventive',
      defaultInterval: { value: 6, unit: 'months' },
      laborRequirements: [
        { role: 'Security Analyst', count: 2, hourlyRate: 130 },
        { role: 'Auditor', count: 1, hourlyRate: 140 },
      ],
      estimatedDuration: { min: 7, typical: 14, max: 21, unit: 'days' },
      skillsRequired: ['cybersecurity', 'audit', 'compliance'],
      partsCommonly: ['penetration_testing_tools', 'audit_software'],
    },
  ],

  riskFactors: [
    { id: 'credit_concentration', name: 'Credit Concentration Risk', description: 'Risk from concentrated exposures', category: 'financial', weight: 0.30, calculationMethod: 'exponential', inputs: ['hhi_index', 'top_10_exposure'] },
    { id: 'liquidity_risk', name: 'Liquidity Risk', description: 'Risk of funding shortfall', category: 'financial', weight: 0.25, calculationMethod: 'threshold', inputs: ['lcr', 'nsfr', 'cash_position'] },
    { id: 'model_risk', name: 'Model Risk', description: 'Risk from model errors', category: 'operational', weight: 0.20, calculationMethod: 'linear', inputs: ['backtest_exceptions', 'model_age'] },
    { id: 'cyber_risk', name: 'Cyber Risk', description: 'Risk of cyber attack', category: 'operational', weight: 0.25, calculationMethod: 'exponential', inputs: ['vulnerability_score', 'incident_history'] },
  ],

  complianceStandards: [
    {
      id: 'basel_iii',
      name: 'Basel III Capital Requirements',
      shortName: 'Basel III',
      description: 'International banking capital standards',
      authority: 'Basel Committee on Banking Supervision',
      region: ['Global'],
      requirements: [
        { id: 'cet1_ratio', name: 'CET1 Ratio', description: 'Common equity tier 1 ratio', kpiId: 'cet1_ratio', threshold: 4.5, operator: 'gte', severity: 'critical' },
        { id: 'lcr', name: 'Liquidity Coverage Ratio', description: 'Short-term liquidity', kpiId: 'lcr', threshold: 100, operator: 'gte', severity: 'critical' },
      ],
      reportingFrequency: 'quarterly',
    },
    {
      id: 'sox',
      name: 'Sarbanes-Oxley Act',
      shortName: 'SOX',
      description: 'Financial reporting and controls',
      authority: 'SEC',
      region: ['US'],
      requirements: [
        { id: 'internal_controls', name: 'Internal Controls', description: 'Effective internal controls', kpiId: 'control_effectiveness', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'audit_trail', name: 'Audit Trail', description: 'Complete transaction audit trail', kpiId: 'audit_completeness', threshold: 100, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'annual',
    },
  ],

  visualization: {
    primaryColor: '#10b981',
    secondaryColor: '#3b82f6',
    healthyColor: '#10b981',
    warningColor: '#f59e0b',
    criticalColor: '#ef4444',
    chartDefaults: {
      showGrid: true,
      showLegend: true,
      animationDuration: 300,
    },
  },

  narrativeTemplates: [
    {
      id: 'risk_summary',
      name: 'Risk Summary',
      context: 'asset_health',
      audience: ['executive', 'manager', 'analyst'],
      template: 'Your {assetType} "{assetName}" is currently {healthStatus}. {trendDescription} Key risk metrics: Default rate {defaultRate}%, Expected Loss ${expectedLoss}M. {recommendationText}',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'healthStatus', type: 'terminology' },
        { name: 'trendDescription', type: 'metric' },
        { name: 'defaultRate', type: 'percentage' },
        { name: 'expectedLoss', type: 'currency' },
        { name: 'recommendationText', type: 'metric' },
      ],
    },
    {
      id: 'early_warning',
      name: 'Early Warning Alert',
      context: 'prediction',
      audience: ['manager', 'analyst'],
      template: '{prediction} for {assetType} "{assetName}": {predictionDetails}. Probability of {failure}: {probability}%. Recommended action: {recommendation}',
      variables: [
        { name: 'prediction', type: 'terminology' },
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'predictionDetails', type: 'metric' },
        { name: 'failure', type: 'terminology' },
        { name: 'probability', type: 'percentage' },
        { name: 'recommendation', type: 'metric' },
      ],
    },
  ],

  features: {
    enableBenchmarking: true,
    enableWhatIf: true,
    enableCollaboration: true,
    enableCompliance: true,
    enableKnowledgeCapture: true,
  },

  defaults: {
    timeRange: '30d',
    refreshInterval: 300,
    defaultDashboard: 'command-center',
  },

  // =============================================================================
  // PROFESSIONAL ROLES (Financial Data Center / Trading Infrastructure)
  // =============================================================================
  professionalRoles: [
    // SME Roles
    {
      id: 'trading_systems_sre',
      category: 'sme' as const,
      name: 'Trading Systems SRE',
      shortName: 'TSSRE',
      description: 'Owns reliability for trading / matching / settlement systems — p99 latency, error budgets, DR posture',
      responsibilities: [
        'Trading-system latency + error-budget management',
        'Sev 1 / 2 incident response and post-mortems',
        'DR runbook validation and chaos engineering',
        'Capacity planning for market-open + close peaks',
        'On-call rotation and SLI/SLO definition',
      ],
      focusAreas: ['p99_latency', 'transaction_success_rate', 'dr_rto', 'sli_slo', 'sev1_incident_rate'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'risk', 'action_items'] as const,
      },
      decisionAuthority: ['incident_escalation', 'capacity_changes', 'rollback_triggers', 'failover_initiation'],
      reportingTo: 'VP Engineering / CTO',
      icon: 'Activity',
    },
    {
      id: 'finra_sox_compliance_officer',
      category: 'analyst' as const,
      name: 'FINRA / SOX Compliance Officer',
      shortName: 'FSCO',
      description: 'Owns regulatory posture for financial-services IT: SEC Reg-SCI, FINRA cyber, SOX 302/404, PCI-DSS v4.0',
      responsibilities: [
        'Reg-SCI reportable-event evaluation and SEC notification',
        'FINRA cybersecurity guidance compliance',
        'SOX 302 / 404 controls testing',
        'PCI-DSS v4.0 evidence collection',
        'NIST CSF maturity assessment + ISO 27001 internal audit',
      ],
      focusAreas: ['reg_sci_reportable', 'finra_cyber', 'sox_controls', 'pci_dss_v4', 'nist_csf_maturity'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['risk', 'business_impact', 'action_items'] as const,
      },
      decisionAuthority: ['regulator_notification_drafts', 'control_remediation_plans', 'evidence_gating'],
      reportingTo: 'Chief Compliance Officer / CISO',
      icon: 'Shield',
    },
    {
      id: 'dc_infrastructure_engineer',
      category: 'sme' as const,
      name: 'Data Center Infrastructure Engineer',
      shortName: 'DCIE',
      description: 'Designs, deploys, and maintains physical data center infrastructure including power, cooling, and rack systems',
      responsibilities: [
        'Data center capacity planning and design',
        'Power and cooling system architecture',
        'Infrastructure redundancy and resilience',
        'Equipment lifecycle management',
        'Vendor evaluation and procurement',
      ],
      focusAreas: ['power_utilization', 'cooling_efficiency', 'rack_density', 'redundancy_levels', 'pue'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'risk', 'action_items'] as const,
      },
      decisionAuthority: ['infrastructure_design', 'equipment_selection', 'capacity_allocation'],
      reportingTo: 'VP of Infrastructure',
      icon: 'Server',
    },
    // Operations Roles
    {
      id: 'it_operations_manager',
      category: 'executive' as const,
      name: 'IT Operations Manager',
      shortName: 'ITOM',
      description: 'Oversees day-to-day data center operations, staffing, budgets, and SLA compliance',
      responsibilities: [
        'Operations team leadership and scheduling',
        'SLA monitoring and compliance',
        'Budget and cost management',
        'Incident management and escalation',
        'Change management oversight',
      ],
      focusAreas: ['system_availability', 'sla_compliance', 'operational_costs', 'incident_resolution', 'team_utilization'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['business_impact', 'action_items', 'risk'] as const,
      },
      decisionAuthority: ['operations_budget', 'staffing_levels', 'maintenance_scheduling', 'vendor_contracts'],
      reportingTo: 'CTO',
      icon: 'Users',
    },
    // Technician Roles
    {
      id: 'hardware_maintenance_technician',
      category: 'technician' as const,
      name: 'Hardware Maintenance Technician',
      shortName: 'HMT',
      description: 'Performs hands-on server, storage, and network hardware repairs, replacements, and preventive maintenance',
      responsibilities: [
        'Server and storage hardware troubleshooting',
        'Hot-swap component replacement (drives, DIMMs, PSUs)',
        'Cable management and rack maintenance',
        'Preventive maintenance execution',
        'Work order completion and asset tagging',
      ],
      focusAreas: ['fault_codes', 'hardware_diagnostics', 'parts_needed', 'safety_procedures', 'esd_protocols'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: false,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'action_items'] as const,
      },
      decisionAuthority: ['component_replacement', 'immediate_equipment_isolation', 'parts_selection'],
      reportingTo: 'IT Operations Manager',
      icon: 'Wrench',
    },
    // Analyst Roles
    {
      id: 'network_reliability_engineer',
      category: 'analyst' as const,
      name: 'Network Reliability Engineer',
      shortName: 'NRE',
      description: 'Monitors network performance, analyzes failure trends, and drives reliability improvements across the trading infrastructure',
      responsibilities: [
        'Network performance monitoring and analysis',
        'Latency measurement and optimization',
        'Failure trend analysis and reporting',
        'Capacity planning for network growth',
        'Disaster recovery network design',
      ],
      focusAreas: ['network_latency', 'packet_loss', 'link_utilization', 'failover_times', 'error_rates'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['technical_details', 'business_impact', 'risk'] as const,
      },
      decisionAuthority: ['network_topology_changes', 'monitoring_thresholds', 'vendor_escalation'],
      reportingTo: 'VP of Infrastructure',
      icon: 'Network',
    },
    // Compliance Roles
    {
      id: 'compliance_audit_specialist',
      category: 'analyst' as const,
      name: 'Compliance & Audit Specialist',
      shortName: 'CAS',
      description: 'Ensures data center operations meet financial regulatory requirements, conducts audits, and manages compliance documentation',
      responsibilities: [
        'Regulatory compliance monitoring (SOX, PCI-DSS, SOC 2)',
        'Internal and external audit coordination',
        'Policy and procedure documentation',
        'Risk assessment and remediation tracking',
        'Evidence collection and report generation',
      ],
      focusAreas: ['compliance_gaps', 'audit_findings', 'control_effectiveness', 'policy_adherence', 'regulatory_changes'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['compliance', 'risk', 'business_impact'] as const,
      },
      decisionAuthority: ['compliance_reporting', 'audit_scope', 'remediation_priorities'],
      reportingTo: 'Chief Compliance Officer',
      icon: 'ClipboardCheck',
    },
  ],

  // =============================================================================
  // PARTS CATALOG (Data Center Equipment)
  // =============================================================================
  partsCatalog: {
    categories: [
      {
        id: 'server_components',
        name: 'Server Components',
        description: 'Memory, storage, and power supply modules for rack-mount servers',
        parts: [
          {
            id: 'dimm_64gb_ddr5',
            partNumber: 'SAM-M393A8G40AB2',
            name: 'Server DIMM 64GB DDR5-4800 ECC',
            description: '64GB DDR5-4800 ECC registered DIMM for enterprise servers',
            category: 'server_components',
            manufacturer: 'Samsung',
            unitCost: 285.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['trading_system', 'payment_gateway', 'risk_model'],
            failureModes: ['memory_ecc_error', 'dimm_failure', 'thermal_throttle'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { capacity: '64GB', type: 'DDR5-4800', ecc: 'Registered' },
          },
          {
            id: 'ssd_nvme_3_84tb',
            partNumber: 'SAM-PM1733-3T84',
            name: 'Enterprise NVMe SSD 3.84TB',
            description: 'U.2 NVMe SSD for high-IOPS trading and analytics workloads',
            category: 'server_components',
            manufacturer: 'Samsung',
            unitCost: 620.00,
            currency: 'USD',
            leadTime: { value: 2, unit: 'days' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['trading_system', 'payment_gateway', 'risk_model'],
            failureModes: ['drive_failure', 'data_corruption', 'wear_level_exceeded'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { capacity: '3.84TB', interface: 'NVMe U.2', iops: '800K read' },
          },
          {
            id: 'ssd_sata_960gb',
            partNumber: 'MX-870EVO-960',
            name: 'Enterprise SATA SSD 960GB',
            description: '2.5" SATA SSD for general-purpose server storage and boot drives',
            category: 'server_components',
            manufacturer: 'Samsung',
            unitCost: 145.00,
            currency: 'USD',
            leadTime: { value: 1, unit: 'days' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['trading_system', 'atm_network', 'payment_gateway'],
            failureModes: ['drive_failure', 'wear_level_exceeded'],
            averageLifespan: { value: 4, unit: 'years' },
            specifications: { capacity: '960GB', interface: 'SATA III', form: '2.5"' },
          },
          {
            id: 'psu_1600w',
            partNumber: 'DEL-PWR-1600W-R',
            name: 'Redundant Server PSU 1600W',
            description: '1600W hot-swap redundant power supply unit for 2U rack servers',
            category: 'server_components',
            manufacturer: 'Dell',
            unitCost: 380.00,
            currency: 'USD',
            leadTime: { value: 2, unit: 'days' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['trading_system', 'payment_gateway', 'risk_model'],
            failureModes: ['psu_failure', 'capacitor_degradation', 'fan_failure'],
            averageLifespan: { value: 6, unit: 'years' },
            specifications: { wattage: '1600W', efficiency: '80+ Titanium', hotSwap: 'yes' },
          },
        ],
      },
      {
        id: 'ups_power',
        name: 'UPS & Power Distribution',
        description: 'Uninterruptible power supply modules, batteries, and power distribution components',
        parts: [
          {
            id: 'ups_battery_module',
            partNumber: 'APC-RBC44',
            name: 'UPS Battery Module 48V',
            description: 'Hot-swappable sealed lead-acid battery module for rack-mount UPS',
            category: 'ups_power',
            manufacturer: 'APC / Schneider Electric',
            unitCost: 750.00,
            currency: 'USD',
            leadTime: { value: 5, unit: 'days' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['trading_system', 'payment_gateway', 'atm_network'],
            failureModes: ['battery_capacity_loss', 'cell_failure', 'thermal_runaway'],
            averageLifespan: { value: 3, unit: 'years' },
            specifications: { voltage: '48V', chemistry: 'VRLA', hotSwap: 'yes' },
          },
          {
            id: 'ups_inverter_board',
            partNumber: 'EAT-INV-20KVA',
            name: 'UPS Inverter Board 20kVA',
            description: 'Replacement inverter power board for modular UPS systems',
            category: 'ups_power',
            manufacturer: 'Eaton',
            unitCost: 2800.00,
            currency: 'USD',
            leadTime: { value: 10, unit: 'days' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['trading_system', 'payment_gateway'],
            failureModes: ['inverter_failure', 'igbt_fault', 'output_voltage_error'],
            averageLifespan: { value: 8, unit: 'years' },
            specifications: { rating: '20kVA', topology: 'Double conversion' },
          },
          {
            id: 'ups_capacitor_bank',
            partNumber: 'APC-CAP-BNK-3PH',
            name: 'UPS DC Bus Capacitor Bank',
            description: 'Electrolytic capacitor bank for UPS DC bus filtering and energy storage',
            category: 'ups_power',
            manufacturer: 'APC / Schneider Electric',
            unitCost: 1200.00,
            currency: 'USD',
            leadTime: { value: 14, unit: 'days' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['trading_system', 'payment_gateway'],
            failureModes: ['capacitor_dry_out', 'esr_increase', 'capacitance_loss'],
            averageLifespan: { value: 7, unit: 'years' },
            specifications: { type: 'Electrolytic', voltage: '800VDC' },
          },
          {
            id: 'pdu_breaker_30a',
            partNumber: 'SRV-PDU-BRK-30A',
            name: 'PDU Circuit Breaker 30A',
            description: 'Replacement 30A branch breaker for intelligent rack PDU',
            category: 'ups_power',
            manufacturer: 'Server Technology',
            unitCost: 95.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['trading_system', 'payment_gateway', 'atm_network'],
            failureModes: ['breaker_trip_failure', 'overheating', 'contact_wear'],
            averageLifespan: { value: 10, unit: 'years' },
            specifications: { rating: '30A', poles: 1, type: 'Thermal-magnetic' },
          },
          {
            id: 'surge_protector_rack',
            partNumber: 'TRP-SMART1500RM',
            name: 'Rack-Mount Surge Protector / Line Conditioner',
            description: '1U rack-mount surge protector with EMI/RFI filtering for sensitive equipment',
            category: 'ups_power',
            manufacturer: 'Tripp Lite',
            unitCost: 320.00,
            currency: 'USD',
            leadTime: { value: 2, unit: 'days' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['trading_system', 'payment_gateway', 'risk_model'],
            failureModes: ['mov_degradation', 'filter_failure'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { formFactor: '1U rack', outlets: 12, joules: 2400 },
          },
        ],
      },
      {
        id: 'cooling',
        name: 'Cooling System Components',
        description: 'CRAC/CRAH units, in-row coolers, and precision cooling parts',
        parts: [
          {
            id: 'crac_compressor',
            partNumber: 'LIE-COMP-15T',
            name: 'CRAC Compressor 15-Ton Scroll',
            description: 'Scroll compressor for computer room air conditioning units',
            category: 'cooling',
            manufacturer: 'Liebert / Vertiv',
            unitCost: 4500.00,
            currency: 'USD',
            leadTime: { value: 14, unit: 'days' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['trading_system', 'payment_gateway', 'risk_model'],
            failureModes: ['compressor_failure', 'refrigerant_leak', 'motor_burnout'],
            averageLifespan: { value: 10, unit: 'years' },
            specifications: { capacity: '15 tons', type: 'Scroll', refrigerant: 'R-410A' },
          },
          {
            id: 'fan_assembly_crac',
            partNumber: 'LIE-FAN-EC-24',
            name: 'CRAC EC Fan Assembly 24"',
            description: 'Electronically commutated variable-speed fan assembly for CRAC/CRAH units',
            category: 'cooling',
            manufacturer: 'Liebert / Vertiv',
            unitCost: 1850.00,
            currency: 'USD',
            leadTime: { value: 7, unit: 'days' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['trading_system', 'payment_gateway', 'risk_model'],
            failureModes: ['fan_motor_failure', 'bearing_wear', 'blade_imbalance'],
            averageLifespan: { value: 7, unit: 'years' },
            specifications: { diameter: '24"', type: 'EC variable speed', airflow: '8000 CFM' },
          },
          {
            id: 'refrigerant_r410a',
            partNumber: 'HON-R410A-25',
            name: 'Refrigerant R-410A 25lb Cylinder',
            description: 'R-410A refrigerant for precision cooling systems, EPA-certified',
            category: 'cooling',
            manufacturer: 'Honeywell',
            unitCost: 185.00,
            currency: 'USD',
            leadTime: { value: 1, unit: 'days' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['trading_system', 'payment_gateway'],
            failureModes: ['refrigerant_leak'],
            specifications: { type: 'R-410A', weight: '25 lbs', epaClass: 'Section 608' },
          },
        ],
      },
      {
        id: 'network',
        name: 'Network Infrastructure',
        description: 'Switch optics, cables, and network interface components',
        parts: [
          {
            id: 'sfp_10g_sr',
            partNumber: 'CIS-SFP-10G-SR',
            name: 'SFP+ 10G SR Transceiver',
            description: '10 Gigabit short-range SFP+ multi-mode fiber transceiver',
            category: 'network',
            manufacturer: 'Cisco',
            unitCost: 85.00,
            currency: 'USD',
            leadTime: { value: 1, unit: 'days' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['trading_system', 'payment_gateway', 'atm_network'],
            failureModes: ['optic_degradation', 'transceiver_failure', 'dom_alarm'],
            averageLifespan: { value: 7, unit: 'years' },
            specifications: { speed: '10Gbps', wavelength: '850nm', reach: '300m MMF' },
          },
          {
            id: 'sfp28_25g_lr',
            partNumber: 'CIS-SFP-25G-LR-S',
            name: 'SFP28 25G LR Transceiver',
            description: '25 Gigabit long-range SFP28 single-mode fiber transceiver for spine links',
            category: 'network',
            manufacturer: 'Cisco',
            unitCost: 290.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['trading_system', 'payment_gateway'],
            failureModes: ['optic_degradation', 'transceiver_failure'],
            averageLifespan: { value: 7, unit: 'years' },
            specifications: { speed: '25Gbps', wavelength: '1310nm', reach: '10km SMF' },
          },
          {
            id: 'dac_cable_25g',
            partNumber: 'MEL-MCP2M00-A003',
            name: 'DAC Cable 25G 3m',
            description: '25GbE SFP28 direct-attach copper cable for low-latency top-of-rack connections',
            category: 'network',
            manufacturer: 'Mellanox / NVIDIA',
            unitCost: 55.00,
            currency: 'USD',
            leadTime: { value: 1, unit: 'days' as const },
            criticalityLevel: 'standard' as const,
            compatibleAssetTypes: ['trading_system', 'payment_gateway'],
            failureModes: ['cable_damage', 'connector_wear'],
            averageLifespan: { value: 10, unit: 'years' },
            specifications: { speed: '25Gbps', length: '3m', type: 'Passive DAC' },
          },
        ],
      },
    ],
  },

  // =============================================================================
  // ROOT CAUSE INTELLIGENCE (Financial Data Center Infrastructure)
  // =============================================================================
  rootCauseLibrary: {
    categories: [
      {
        id: 'power_supply',
        name: 'Power Supply Failures',
        description: 'Root causes related to UPS, PDU, and server power supply failures',
        icon: 'Zap',
        color: '#ef4444',
        rootCauses: [
          {
            id: 'ups_battery_degradation',
            name: 'UPS Battery Degradation',
            description: 'Progressive capacity loss in UPS battery modules due to age, temperature, or cycling stress',
            category: 'power_supply',
            symptoms: [
              'Reduced backup runtime reported by UPS',
              'Battery alarm or replace-battery indicator',
              'Elevated battery temperature',
              'Increased charge time',
              'UPS self-test failures',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Run UPS battery self-test and record runtime',
                expectedResult: 'Runtime should be at least 80% of original specification',
                tools: ['UPS management software', 'SNMP monitoring'],
                duration: 15,
                skillLevel: 'basic' as const,
              },
              {
                order: 2,
                action: 'Measure individual battery module voltages under load',
                expectedResult: 'All modules within 0.5V of each other; no module below 10.5V (12V nominal)',
                tools: ['Digital multimeter', 'Battery load tester'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Check battery ambient temperature and ventilation',
                expectedResult: 'Temperature between 20-25°C; adequate airflow around battery cabinet',
                tools: ['IR thermometer', 'Temperature logger'],
                duration: 10,
                skillLevel: 'basic' as const,
              },
            ],
            preventiveActions: [
              'Quarterly UPS battery self-tests',
              'Annual battery impedance testing',
              'Maintain battery room temperature at 20-25°C',
              'Replace batteries on manufacturer-recommended schedule (3-5 years)',
              'Monitor float voltage and current continuously',
            ],
            typicalProbability: 0.20,
            affectedComponents: ['ups_batteries', 'ups_charger', 'ups_inverter'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 1, typical: 2, max: 4, unit: 'hours' as const },
            costRange: { min: 750, max: 6000, currency: 'USD' },
            industryReferences: ['IEEE 1188', 'NFPA 111'],
          },
          {
            id: 'psu_capacitor_failure',
            name: 'Server PSU Capacitor Failure',
            description: 'Electrolytic capacitor dry-out or bulging in server power supplies causing intermittent shutdowns',
            category: 'power_supply',
            symptoms: [
              'Intermittent server reboots',
              'Audible clicking or buzzing from PSU',
              'Visible capacitor bulging or leaking',
              'Power ripple out of spec on 12V rail',
              'PSU fan speed fluctuations',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Check server IPMI/iDRAC logs for power events',
                expectedResult: 'Identify power fault timestamps and frequency',
                tools: ['IPMI/iDRAC interface', 'DCIM software'],
                duration: 10,
                skillLevel: 'basic' as const,
              },
              {
                order: 2,
                action: 'Swap to redundant PSU and monitor for recurrence',
                expectedResult: 'If issue stops, original PSU is confirmed faulty',
                tools: ['Replacement PSU module'],
                duration: 15,
                skillLevel: 'basic' as const,
              },
              {
                order: 3,
                action: 'Visually inspect removed PSU for capacitor damage',
                expectedResult: 'Look for bulging, leaking, or discolored capacitors',
                tools: ['ESD-safe workbench', 'Magnifying glass'],
                duration: 10,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Monitor PSU health via IPMI/iDRAC alerts',
              'Replace PSUs proactively at 5-year intervals',
              'Ensure adequate server inlet air temperature',
              'Avoid operating near maximum PSU wattage continuously',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['server_psu', 'server_motherboard'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 15, typical: 30, max: 60, unit: 'minutes' as const },
            costRange: { min: 300, max: 800, currency: 'USD' },
          },
        ],
      },
      {
        id: 'cooling_degradation',
        name: 'Cooling System Degradation',
        description: 'Root causes related to CRAC/CRAH units, in-row coolers, and airflow management',
        icon: 'Thermometer',
        color: '#f59e0b',
        rootCauses: [
          {
            id: 'crac_compressor_failure',
            name: 'CRAC Compressor Failure',
            description: 'Scroll or reciprocating compressor failure in precision cooling units due to refrigerant issues, mechanical wear, or electrical faults',
            category: 'cooling_degradation',
            symptoms: [
              'Rising server inlet temperatures',
              'CRAC unit alarm or lockout',
              'Unusual compressor noise (rattling, grinding)',
              'High head pressure or low suction pressure',
              'Compressor short-cycling',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Check CRAC unit alarm panel for fault codes',
                expectedResult: 'Identify specific compressor fault (high pressure, low pressure, motor overload)',
                tools: ['CRAC control panel', 'BMS interface'],
                duration: 5,
                skillLevel: 'basic' as const,
              },
              {
                order: 2,
                action: 'Measure suction and discharge pressures',
                expectedResult: 'Suction 60-70 psig, discharge 200-300 psig for R-410A (varies by ambient)',
                tools: ['Refrigerant manifold gauge set'],
                duration: 15,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Measure compressor winding resistance and insulation',
                expectedResult: 'Windings balanced within 5%; insulation >1 MΩ',
                tools: ['Digital multimeter', 'Megohmmeter'],
                duration: 20,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 4,
                action: 'Check compressor oil level and condition',
                expectedResult: 'Oil at proper sight glass level, no discoloration',
                tools: ['Sight glass inspection', 'Oil sample kit'],
                duration: 10,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Semi-annual CRAC preventive maintenance',
              'Monitor refrigerant pressures continuously via BMS',
              'Annual refrigerant leak testing',
              'Maintain clean condenser coils',
              'Log compressor run hours and schedule overhauls',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['crac_compressor', 'crac_condenser', 'crac_evaporator', 'refrigerant_piping'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 4, typical: 8, max: 24, unit: 'hours' as const },
            costRange: { min: 2000, max: 8000, currency: 'USD' },
            industryReferences: ['ASHRAE TC 9.9', 'ASHRAE Guideline 36'],
          },
          {
            id: 'airflow_bypass',
            name: 'Hot/Cold Aisle Airflow Bypass',
            description: 'Mixing of hot exhaust air with cold supply air due to missing blanking panels, cable cutout gaps, or raised floor tile displacement',
            category: 'cooling_degradation',
            symptoms: [
              'Hot spots at specific rack positions',
              'Server inlet temperatures exceeding 27°C',
              'Inconsistent temperature across rack front',
              'CRAC units working harder than expected',
              'PUE trending upward',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform thermal survey with IR camera across hot and cold aisles',
                expectedResult: 'Identify temperature differentials and bypass locations',
                tools: ['IR thermal camera', 'Temperature data loggers'],
                duration: 60,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Inspect blanking panels and cable cutout seals in all racks',
                expectedResult: 'All unused U-spaces blanked; cable openings sealed',
                tools: ['Flashlight', 'Blanking panel inventory'],
                duration: 45,
                skillLevel: 'basic' as const,
              },
            ],
            preventiveActions: [
              'Install blanking panels in all unused rack spaces',
              'Seal cable cutouts with brush grommets',
              'Enforce hot/cold aisle containment',
              'Quarterly thermal audits',
              'Maintain raised floor tile integrity',
            ],
            typicalProbability: 0.18,
            affectedComponents: ['rack_layout', 'blanking_panels', 'floor_tiles', 'containment_doors'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 1, typical: 4, max: 8, unit: 'hours' as const },
            costRange: { min: 100, max: 2000, currency: 'USD' },
            industryReferences: ['ASHRAE TC 9.9 Thermal Guidelines'],
          },
        ],
      },
      {
        id: 'storage_memory',
        name: 'Storage & Memory Failures',
        description: 'Root causes related to server DIMM, SSD/NVMe, and storage array failures',
        icon: 'HardDrive',
        color: '#8b5cf6',
        rootCauses: [
          {
            id: 'nvme_wear_out',
            name: 'NVMe/SSD Wear-Level Exhaustion',
            description: 'Flash cells reach maximum program/erase cycle limit, causing write endurance exhaustion and drive entering read-only mode',
            category: 'storage_memory',
            symptoms: [
              'SMART wear-level indicator approaching threshold',
              'Increasing write latency',
              'Drive entering read-only or write-protect mode',
              'Reallocated sector count increasing',
              'Controller-reported media errors',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Query drive SMART/NVMe health via smartctl or vendor tool',
                expectedResult: 'Percentage used < 90%; no critical warnings',
                tools: ['smartctl', 'Vendor SSD management utility'],
                duration: 5,
                skillLevel: 'basic' as const,
              },
              {
                order: 2,
                action: 'Review write amplification factor and host writes',
                expectedResult: 'WAF < 3; total bytes written within TBW rating',
                tools: ['nvme-cli', 'Vendor dashboard'],
                duration: 10,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Check RAID or storage controller for drive degradation alerts',
                expectedResult: 'No predictive failure or degraded states',
                tools: ['RAID controller CLI', 'Storage management software'],
                duration: 10,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Monitor SMART wear-level indicators via DCIM',
              'Set automated alerts at 80% wear level',
              'Right-size drive endurance class for workload (read-intensive vs. mixed-use)',
              'Implement proactive drive replacement at 85% life consumed',
              'Use write caching and tiering to reduce flash writes',
            ],
            typicalProbability: 0.10,
            affectedComponents: ['nvme_ssd', 'sata_ssd', 'storage_controller'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 15, typical: 45, max: 120, unit: 'minutes' as const },
            costRange: { min: 150, max: 1200, currency: 'USD' },
          },
          {
            id: 'dimm_ecc_errors',
            name: 'DIMM ECC Correctable/Uncorrectable Errors',
            description: 'Memory module generating correctable ECC errors trending toward uncorrectable failures, risking data corruption or system crash',
            category: 'storage_memory',
            symptoms: [
              'Increasing correctable ECC error count in system logs',
              'Machine check exceptions (MCE)',
              'Unexpected application crashes or data corruption',
              'Server performance degradation',
              'BIOS/UEFI memory training failures on reboot',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review IPMI/iDRAC system event log for memory errors',
                expectedResult: 'Identify specific DIMM slot and error frequency',
                tools: ['IPMI/iDRAC interface', 'ipmitool'],
                duration: 10,
                skillLevel: 'basic' as const,
              },
              {
                order: 2,
                action: 'Run vendor memory diagnostics (e.g., Dell ePSA, HP Insight)',
                expectedResult: 'Identify failing DIMM module and error pattern',
                tools: ['Vendor diagnostics', 'MemTest86'],
                duration: 60,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Reseat DIMM and inspect contacts for contamination',
                expectedResult: 'Clean gold contacts; proper seating in slot',
                tools: ['ESD wrist strap', 'Isopropyl alcohol', 'Lint-free cloth'],
                duration: 15,
                skillLevel: 'basic' as const,
              },
            ],
            preventiveActions: [
              'Enable DIMM error alerting in DCIM/monitoring',
              'Set threshold alerts at >10 correctable errors per day',
              'Proactively replace DIMMs exceeding error thresholds',
              'Maintain proper server inlet temperatures',
              'Use memory mirroring or sparing for critical trading systems',
            ],
            typicalProbability: 0.08,
            affectedComponents: ['dimm_module', 'memory_controller', 'cpu_imc'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 15, typical: 30, max: 60, unit: 'minutes' as const },
            costRange: { min: 285, max: 600, currency: 'USD' },
          },
        ],
      },
      {
        id: 'network_faults',
        name: 'Network Infrastructure Faults',
        description: 'Root causes related to switches, optics, cabling, and network connectivity in the trading infrastructure',
        icon: 'Network',
        color: '#3b82f6',
        rootCauses: [
          {
            id: 'sfp_optic_degradation',
            name: 'SFP/SFP+ Optic Degradation',
            description: 'Transceiver optical power declining below receiver sensitivity threshold, causing link flaps or CRC errors',
            category: 'network_faults',
            symptoms: [
              'Intermittent link flaps on switch port',
              'Increasing CRC or FCS errors',
              'DOM (Digital Optical Monitoring) power alarms',
              'Transmit or receive power outside spec',
              'Latency spikes on affected links',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Check switch interface counters for errors and resets',
                expectedResult: 'Identify error type and rate (CRC, runts, giants, flaps)',
                tools: ['Switch CLI (show interface)', 'NMS dashboard'],
                duration: 5,
                skillLevel: 'basic' as const,
              },
              {
                order: 2,
                action: 'Read DOM values for TX/RX power, temperature, and voltage',
                expectedResult: 'TX power >-6 dBm, RX power >-14 dBm (for SR); temperature in spec',
                tools: ['Switch CLI (show transceiver)', 'NMS'],
                duration: 5,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Clean fiber connector end-faces and re-test',
                expectedResult: 'Link stabilizes; DOM values return to normal',
                tools: ['Fiber cleaning pen', 'Fiber inspection scope'],
                duration: 15,
                skillLevel: 'basic' as const,
              },
              {
                order: 4,
                action: 'Swap SFP module and re-test link',
                expectedResult: 'If link stabilizes, original SFP is confirmed faulty',
                tools: ['Replacement SFP module'],
                duration: 10,
                skillLevel: 'basic' as const,
              },
            ],
            preventiveActions: [
              'Monitor DOM values via SNMP and set threshold alerts',
              'Use dust caps on all unused fiber ports',
              'Clean fiber connectors during every maintenance window',
              'Track SFP age and replace proactively at 7 years',
              'Stock spare SFPs for each type in use',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['sfp_transceiver', 'fiber_cable', 'switch_port'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 10, typical: 30, max: 60, unit: 'minutes' as const },
            costRange: { min: 85, max: 500, currency: 'USD' },
          },
          {
            id: 'switch_fabric_failure',
            name: 'Network Switch Fabric/Linecard Failure',
            description: 'Switch linecard or switching fabric ASIC failure causing partial or complete traffic loss across multiple ports',
            category: 'network_faults',
            symptoms: [
              'Multiple ports on same linecard go down simultaneously',
              'Switch syslog critical hardware errors',
              'Traffic blackhole on specific VLANs or port ranges',
              'Switch fan or temperature alarms',
              'CRC errors across multiple ports',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review switch syslog and platform health status',
                expectedResult: 'Identify linecard or ASIC fault messages',
                tools: ['Switch CLI (show platform)', 'Syslog server'],
                duration: 10,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Check switch environmental health (temps, fans, power)',
                expectedResult: 'All sensors within normal range',
                tools: ['Switch CLI (show environment)', 'NMS'],
                duration: 5,
                skillLevel: 'basic' as const,
              },
              {
                order: 3,
                action: 'Attempt linecard reseat or online insertion/removal',
                expectedResult: 'Linecard reinitializes and ports recover',
                tools: ['ESD wrist strap'],
                duration: 20,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Deploy redundant switch fabric (e.g., VPC, MLAG, stacking)',
              'Monitor switch hardware health via SNMP traps',
              'Keep switch firmware current with vendor-recommended stable releases',
              'Maintain clean switch cabinet airflow and temperatures',
              'Stock spare linecards for critical spine/core switches',
            ],
            typicalProbability: 0.06,
            affectedComponents: ['switch_linecard', 'switch_fabric_asic', 'switch_backplane'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 30, typical: 120, max: 480, unit: 'minutes' as const },
            costRange: { min: 2000, max: 15000, currency: 'USD' },
            industryReferences: ['TIA-942 Data Center Standards'],
          },
        ],
      },
      {
        id: 'environmental',
        name: 'Environmental Control Issues',
        description: 'Root causes related to humidity, water leak, fire suppression, and physical environment in the data center',
        icon: 'CloudRain',
        color: '#06b6d4',
        rootCauses: [
          {
            id: 'humidity_excursion',
            name: 'Humidity Excursion',
            description: 'Relative humidity falling outside ASHRAE recommended range (20-80% RH) causing static discharge risk or condensation',
            category: 'environmental',
            symptoms: [
              'BMS humidity alarm (too low or too high)',
              'Static discharge incidents',
              'Condensation visible on cold surfaces',
              'Increased ESD-related component failures',
              'Corrosion on connectors or contacts',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review BMS humidity trend data for affected zones',
                expectedResult: 'Identify excursion magnitude and duration',
                tools: ['BMS interface', 'Environmental monitoring sensors'],
                duration: 10,
                skillLevel: 'basic' as const,
              },
              {
                order: 2,
                action: 'Verify humidifier/dehumidifier operation and settings',
                expectedResult: 'Humidifier is operational and setpoint is correct (45% RH typical)',
                tools: ['CRAC humidifier inspection'],
                duration: 20,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Check for water intrusion sources (roof, pipes, condensate drains)',
                expectedResult: 'No active leaks or standing water',
                tools: ['Flashlight', 'Moisture meter'],
                duration: 30,
                skillLevel: 'basic' as const,
              },
            ],
            preventiveActions: [
              'Maintain CRAC humidifier/dehumidifier function',
              'Set BMS alerts at ASHRAE A1 boundaries (20-80% RH)',
              'Quarterly calibration of humidity sensors',
              'Ensure vapor barriers are intact',
              'Monitor dew point to prevent condensation',
            ],
            typicalProbability: 0.10,
            affectedComponents: ['humidifier', 'crac_unit', 'bms_sensors', 'server_components'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 1, typical: 4, max: 12, unit: 'hours' as const },
            costRange: { min: 200, max: 3000, currency: 'USD' },
            industryReferences: ['ASHRAE TC 9.9 A1 Envelope', 'TIA-942'],
          },
          {
            id: 'water_leak_detection',
            name: 'Water Leak Under Raised Floor',
            description: 'Water intrusion from condensate drain overflow, chilled water pipe leak, or external source reaching IT equipment',
            category: 'environmental',
            symptoms: [
              'Water leak detection sensor alarm',
              'Standing water visible under raised floor tiles',
              'Increased humidity in affected zone',
              'Corrosion on underfloor cable trays',
              'CRAC condensate pan overflow',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Identify alarm location from leak detection system zone map',
                expectedResult: 'Pinpoint leak to specific floor zone or CRAC unit',
                tools: ['Leak detection panel', 'Zone map'],
                duration: 5,
                skillLevel: 'basic' as const,
              },
              {
                order: 2,
                action: 'Lift raised floor tiles and trace water source',
                expectedResult: 'Identify source (condensate, chilled water pipe, roof intrusion)',
                tools: ['Floor tile lifter', 'Flashlight'],
                duration: 30,
                skillLevel: 'basic' as const,
              },
              {
                order: 3,
                action: 'Isolate source and deploy containment',
                expectedResult: 'Water flow stopped or contained; no equipment at risk',
                tools: ['Pipe wrench', 'Wet vacuum', 'Absorbent materials'],
                duration: 60,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Install continuous rope-style leak detection under all CRAC units and pipes',
              'Inspect condensate drain pans monthly',
              'Flush condensate drains quarterly',
              'Ensure chilled water pipe insulation is intact to prevent condensation',
              'Test leak detection system annually',
            ],
            typicalProbability: 0.08,
            affectedComponents: ['raised_floor', 'chilled_water_piping', 'crac_condensate', 'cable_trays'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 30, typical: 120, max: 480, unit: 'minutes' as const },
            costRange: { min: 500, max: 10000, currency: 'USD' },
            industryReferences: ['TIA-942', 'NFPA 75'],
          },
        ],
      },
      {
        id: 'firmware_software',
        name: 'Firmware/Software Faults',
        description: 'Root causes related to firmware bugs, configuration drift, and software failures in data center infrastructure',
        icon: 'Code',
        color: '#10b981',
        rootCauses: [
          {
            id: 'firmware_bug',
            name: 'Firmware Bug / Known Defect',
            description: 'Server BIOS, BMC, drive, or switch firmware containing known defects that cause crashes, data corruption, or security vulnerabilities',
            category: 'firmware_software',
            symptoms: [
              'Intermittent system crashes or hangs',
              'Known CVE vulnerability flagged by scanner',
              'Vendor advisory matching installed firmware version',
              'Unexplained hardware errors in logs',
              'Features not working as documented',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Inventory current firmware versions across fleet',
                expectedResult: 'Identify devices running versions with known issues',
                tools: ['DCIM firmware inventory', 'Vendor support portal'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Cross-reference versions against vendor advisory database',
                expectedResult: 'Match installed versions to known defects or CVEs',
                tools: ['Vendor advisory portal', 'CVE database'],
                duration: 20,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Validate fix availability and compatibility',
                expectedResult: 'Confirm updated firmware resolves issue and is compatible',
                tools: ['Vendor release notes', 'Test environment'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Maintain firmware inventory in DCIM/CMDB',
              'Subscribe to vendor security advisories',
              'Establish quarterly firmware review cycle',
              'Test firmware updates in staging environment before production',
              'Automate firmware compliance scanning',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['server_bios', 'bmc_firmware', 'drive_firmware', 'switch_firmware'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 30, typical: 120, max: 480, unit: 'minutes' as const },
            costRange: { min: 0, max: 2000, currency: 'USD' },
            industryReferences: ['NIST SP 800-147', 'PCI-DSS v4.0 Requirement 6'],
          },
          {
            id: 'memory_leak',
            name: 'Application Memory Leak',
            description: 'Trading or payment application fails to release allocated memory, causing progressive performance degradation and eventual out-of-memory crashes',
            category: 'firmware_software',
            symptoms: [
              'Steadily increasing process memory (RSS) over time',
              'Application performance degradation',
              'Out-of-memory (OOM) kills in system logs',
              'Garbage collection pauses growing longer (JVM)',
              'Service restarts temporarily resolve the issue',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Monitor process memory consumption over 24-48 hour window',
                expectedResult: 'Identify linear or exponential memory growth pattern',
                tools: ['APM tools (Datadog, Dynatrace)', 'Prometheus/Grafana'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Generate and analyze heap dump during high-memory state',
                expectedResult: 'Identify object types consuming excessive memory',
                tools: ['jmap', 'Eclipse MAT', 'Heap analyzer'],
                duration: 60,
                skillLevel: 'advanced' as const,
              },
              {
                order: 3,
                action: 'Correlate leak with specific API calls or transaction types',
                expectedResult: 'Identify code path or library causing the leak',
                tools: ['APM distributed tracing', 'Application logs'],
                duration: 60,
                skillLevel: 'advanced' as const,
              },
            ],
            preventiveActions: [
              'Implement continuous memory monitoring with alerting',
              'Conduct code reviews focused on resource lifecycle management',
              'Run load tests with memory profiling before production deploys',
              'Set memory limits and OOM-kill policies in container orchestration',
              'Establish automated service restart thresholds as a safety net',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['application', 'jvm', 'container_runtime'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 2, typical: 8, max: 24, unit: 'hours' as const },
            costRange: { min: 1000, max: 10000, currency: 'USD' },
          },
        ],
      },
    ],
    commonPatterns: [
      {
        id: 'cascading_failure',
        name: 'Cascading System Failure',
        description: 'A single infrastructure failure (e.g., PDU trip) causing downstream server, storage, and network failures across a zone',
        sequence: ['Initial infrastructure failure', 'Dependent servers lose power or cooling', 'Application failover triggers', 'Service degradation or outage'],
        indicators: ['Multiple simultaneous alerts', 'Error rate spike across services', 'Latency increase'],
        preventionStrategy: 'N+1 redundancy, circuit breakers, automated failover, and zone-aware workload placement',
      },
      {
        id: 'thermal_cascade',
        name: 'Thermal Cascade Event',
        description: 'Loss of cooling capacity causing rising temperatures, leading to server thermal throttling and potential shutdowns',
        sequence: ['CRAC unit failure', 'Server inlet temperatures rise above 35°C', 'CPU thermal throttling begins', 'Server thermal shutdowns if sustained'],
        indicators: ['CRAC alarm', 'Rising inlet temperature trend', 'CPU frequency reduction', 'Increased fan speeds'],
        preventionStrategy: 'N+1 CRAC redundancy, BMS escalation automation, emergency cooling procedures, and thermal containment',
      },
      {
        id: 'correlated_drive_failures',
        name: 'Correlated Drive Failures in Same Batch',
        description: 'Multiple SSDs from the same manufacturing batch failing within a short window due to shared firmware defect or wear pattern',
        sequence: ['First drive failure from batch', 'Second drive failure within days', 'RAID rebuild stress triggers additional failures', 'Data availability at risk'],
        indicators: ['Multiple drive failures with adjacent serial numbers', 'Same firmware version across failures', 'Wear-level convergence'],
        preventionStrategy: 'Diversify drive batches per RAID group, monitor wear-level convergence, proactive batch-aware replacement scheduling',
      },
    ],
  },

  // Labor Rates
  laborRates: {
    roles: [
      { roleId: 'sre', name: 'Site Reliability Engineer', hourlyRate: 100, currency: 'USD', certifications: ['AWS', 'GCP'] },
      { roleId: 'dba', name: 'Database Administrator', hourlyRate: 90, currency: 'USD', certifications: ['Oracle DBA', 'SQL Server'] },
      { roleId: 'systems_engineer', name: 'Systems Engineer', hourlyRate: 85, currency: 'USD', certifications: ['RHCE', 'MCSE'] },
    ],
    overtimeMultiplier: 1.5,
    emergencyMultiplier: 2.5,
    weekendMultiplier: 2.0,
  },
}
